import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Autocomplete,
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Reply as ReplyIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  QuestionAnswer as AnswerIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';

// Pre-defined 20 Message Heads + Other
export const PREDEFINED_MESSAGE_HEADS = [
  'UPI Deletion',
  'Shop Payment Error',
  'Commission Error',
  'Wallet Settlement Issue',
  'Pincode Assignment Request',
  'QR Code Generation Issue',
  'Customer VPA Issue',
  'Acemoney Transaction Dispute',
  'Welfare Balance Discrepancy',
  'Shop Onboarding Approval',
  'Withdrawal Delay',
  'KYC Verification Failure',
  'Daily Settlement Mismatch',
  'Privilege Escalation Request',
  'Account Deactivation Inquiry',
  'System Maintenance Alert',
  'GST/Invoice Issue',
  'Device/App Technical Bug',
  'Bank Account Linking Error',
  'General Support Query',
  'Other',
];

// Level titles and colors
const LEVEL_TITLES = {
  1: 'Super Admin (Level 1)',
  2: 'State Head (Level 2)',
  3: 'State Officer (Level 3)',
  4: 'Q-Pay Centre (Level 4)',
  5: 'Q-Pay Agent (Level 5)',
};

// Short role names (thread bubbles)
const LEVEL_NAMES = {
  1: 'Super Admin',
  2: 'State Head',
  3: 'State Officer',
  4: 'Q-Pay Centre',
  5: 'Q-Pay Agent',
};

const formatIST = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' })} IST`;
};

// Build the conversation: original message + replies in time order.
// Old messages may only carry replyMessage/repliedAt with an empty replies array.
const buildThread = (msg) => {
  if (!msg) return [];
  const thread = [{
    key: 'original',
    senderId: msg.senderId,
    senderName: msg.senderName,
    senderLevel: msg.senderLevel,
    message: msg.message,
    createdAt: msg.createdAt,
    isOriginal: true,
  }];
  const replies = Array.isArray(msg.replies) ? msg.replies : [];
  if (replies.length > 0) {
    replies
      .map((r, idx) => ({ ...r, key: r._id || `reply-${idx}`, _idx: idx }))
      .sort((a, b) => {
        const ta = new Date(a.createdAt).getTime() || 0;
        const tb = new Date(b.createdAt).getTime() || 0;
        return ta - tb || a._idx - b._idx;
      })
      .forEach((r) => thread.push(r));
  } else if (msg.replyMessage) {
    thread.push({ key: 'legacy-reply', message: msg.replyMessage, createdAt: msg.repliedAt, isLegacy: true });
  }
  return thread;
};

const LEVEL_COLORS = {
  1: '#8b5cf6', // purple
  2: '#0284c7', // sky blue
  3: '#ec4899', // pink
  4: '#6366f1', // indigo
  5: '#3b82f6', // blue
};

// Hierarchy matrix: Allowed recipient levels based on sender level
const getAllowedRecipientLevels = (senderLevel) => {
  const lvl = Number(senderLevel || 1);
  switch (lvl) {
    case 1:
      return [2, 3, 4, 5]; // Super Admin messages levels below only (mirrors backend)
    case 2:
      return [1, 3, 4, 5]; // State Head can message SuperAdmin (1) and levels below (3, 4, 5)
    case 3:
      return [1, 2, 4, 5]; // State Officer can message SuperAdmin (1), State Head (2), and levels below (4, 5)
    case 4:
      return [1, 2, 3, 5]; // Q-Pay Centre can message 1, 2, 3, and levels below (5)
    case 5:
      return [1, 2, 3, 4]; // Q-Pay Agent can message superiors 1, 2, 3, 4
    default:
      return [1];
  }
};

const UserMessagesPage = () => {
  const [userLevel, setUserLevel] = useState(1);
  const { can } = usePermissions();
  const [userId, setUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [counts, setCounts] = useState({ level2: 0, level3: 0, level4: 0, level5: 0, unread: 0, total: 0, inbox: 0, sent: 0 });

  // Filters & Tabs
  const [activeTab, setActiveTab] = useState('inbox'); // 'inbox' | 'sent'
  const [senderLevelFilter, setSenderLevelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [messageHeadFilter, setMessageHeadFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');

  // Form state for composing a message
  const [composeOpen, setComposeOpen] = useState(false);
  const [targetLevel, setTargetLevel] = useState(2);
  const [recipientUsers, setRecipientUsers] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [fetchingRecipients, setFetchingRecipients] = useState(false);
  const [messageHead, setMessageHead] = useState('General Support Query');
  const [customSubject, setCustomSubject] = useState('');
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);

  // View / Reply Dialog state
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  // Snackbar feedback
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');

  const showSnack = (msg, severity = 'success') => {
    setSnackMessage(msg);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  // Decode user session info
  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const lvl = Number(decoded.level) || 1;
        setUserLevel(lvl);
        setUserId(decoded.userId || '');

        // Set default target recipient level based on sender level
        const allowed = getAllowedRecipientLevels(lvl);
        setTargetLevel(allowed[0] || 1);
      } catch (err) {
        console.error('Failed to decode auth token:', err);
      }
    }
  }, []);

  // Fetch candidate recipient users when target level changes
  useEffect(() => {
    const fetchTargetUsers = async () => {
      if (targetLevel === 1) {
        setRecipientUsers([]);
        setSelectedRecipient(null);
        return;
      }
      setFetchingRecipients(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/users/by-level`, {
          params: { level: targetLevel, requesterLevel: userLevel, userId },
        });
        if (Array.isArray(response.data)) {
          setRecipientUsers(response.data);
        } else {
          setRecipientUsers([]);
        }
      } catch (err) {
        console.error('Error fetching target recipients:', err);
        setRecipientUsers([]);
      } finally {
        setFetchingRecipients(false);
      }
    };

    if (composeOpen) {
      fetchTargetUsers();
    }
  }, [targetLevel, composeOpen, userLevel, userId]);

  // Fetch messages from backend
  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/user-messages`, {
        params: {
          userId,
          level: userLevel,
          folder: activeTab,
          senderLevel: senderLevelFilter,
          status: statusFilter,
          messageHead: messageHeadFilter,
          search: searchQuery,
          order: sortOrder,
        },
      });

      setMessages(response.data.messages || []);
      if (response.data.counts) {
        setCounts(response.data.counts);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      showSnack('Failed to load messages', 'error');
    } finally {
      setLoading(false);
    }
  }, [userId, userLevel, activeTab, senderLevelFilter, statusFilter, messageHeadFilter, searchQuery, sortOrder]);

  useEffect(() => {
    if (userId || userLevel === 1) {
      fetchMessages();
    }
  }, [fetchMessages, userId, userLevel]);

  // Handle composing & sending a new message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!can('messages.inbox.compose')) return;
    if (!messageText.trim()) {
      showSnack('Message content is required', 'warning');
      return;
    }
    if (messageHead === 'Other' && !customSubject.trim()) {
      showSnack('Please enter a custom subject line for "Other"', 'warning');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API_BASE_URL}/user-messages`, {
        userId,
        recipientLevel: targetLevel,
        recipientId: selectedRecipient ? selectedRecipient._id : null,
        messageHead,
        customSubject: customSubject.trim(),
        message: messageText.trim(),
      });

      showSnack('Message dispatched successfully!');
      setComposeOpen(false);
      setMessageText('');
      setCustomSubject('');
      setMessageHead('General Support Query');
      setSelectedRecipient(null);
      fetchMessages();
    } catch (err) {
      console.error('Error sending message:', err);
      const errorMsg = (err.response && err.response.data && err.response.data.message) ? err.response.data.message : 'Failed to send message';
      showSnack(errorMsg, 'error');
    } finally {
      setSending(false);
    }
  };

  // Handle marking message as read
  const handleMarkAsRead = async (msgId) => {
    try {
      await axios.put(`${API_BASE_URL}/user-messages/${msgId}/read`);
      showSnack('Message marked as read');
      fetchMessages();
      if (selectedMessage && selectedMessage._id === msgId) {
        setSelectedMessage({ ...selectedMessage, status: 'read' });
      }
    } catch (err) {
      console.error('Error marking as read:', err);
      showSnack('Failed to mark message as read', 'error');
    }
  };

  // Handle replying to a message
  const handleSendReply = async () => {
    if (!can('messages.inbox.reply')) return;
    if (!replyText.trim() || !selectedMessage) {
      showSnack('Reply text cannot be empty', 'warning');
      return;
    }

    setReplying(true);
    try {
      const text = replyText.trim();
      const res = await axios.post(`${API_BASE_URL}/user-messages/${selectedMessage._id}/reply`, {
        replyMessage: text,
      });

      const updated = res && res.data && res.data.data;
      setSelectedMessage((prev) => {
        if (!prev) return prev;
        if (updated && Array.isArray(updated.replies)) {
          return {
            ...prev,
            replies: updated.replies,
            replyMessage: updated.replyMessage,
            repliedAt: updated.repliedAt,
            status: updated.status || 'replied',
          };
        }
        // Fallback: append locally so the history stays visible
        const prevReplies = Array.isArray(prev.replies) && prev.replies.length > 0
          ? prev.replies
          : (prev.replyMessage ? [{ message: prev.replyMessage, createdAt: prev.repliedAt }] : []);
        const now = new Date().toISOString();
        return {
          ...prev,
          replies: [...prevReplies, { senderId: userId, senderLevel: userLevel, senderName: 'You', message: text, createdAt: now }],
          replyMessage: text,
          repliedAt: now,
          status: 'replied',
        };
      });
      showSnack('Reply sent successfully!');
      setReplyText('');
      fetchMessages();
    } catch (err) {
      console.error('Error sending reply:', err);
      const errorMsg = (err.response && err.response.data && err.response.data.message) ? err.response.data.message : 'Failed to send reply';
      showSnack(errorMsg, 'error');
    } finally {
      setReplying(false);
    }
  };

  // Handle deleting a message
  const handleDeleteMessage = async (msgId) => {
    if (!can('messages.inbox.delete')) return;
    try {
      await axios.delete(`${API_BASE_URL}/user-messages/${msgId}`);
      showSnack('Message deleted');
      if (selectedMessage && selectedMessage._id === msgId) {
        setViewDialogOpen(false);
      }
      fetchMessages();
    } catch (err) {
      console.error('Error deleting message:', err);
      showSnack('Failed to delete message', 'error');
    }
  };

  // Open detail dialog
  const handleOpenDetail = (msg) => {
    setSelectedMessage(msg);
    setReplyText('');
    setViewDialogOpen(true);
    if (msg.status === 'unread') {
      handleMarkAsRead(msg._id);
    }
  };

  const renderStatusChip = (status) => {
    switch (status) {
      case 'unread':
        return <Chip label="Unread" size="small" color="error" icon={<EmailIcon fontSize="small" />} sx={{ fontWeight: 700 }} />;
      case 'read':
        return <Chip label="Read" size="small" color="info" icon={<MarkEmailReadIcon fontSize="small" />} sx={{ fontWeight: 600 }} />;
      case 'replied':
        return <Chip label="Replied" size="small" color="success" icon={<AnswerIcon fontSize="small" />} sx={{ fontWeight: 700 }} />;
      default:
        return <Chip label={status} size="small" variant="outlined" />;
    }
  };

  const allowedRecipientLevels = getAllowedRecipientLevels(userLevel);

  return (
    <Container maxWidth="xl" sx={{ mt: 1, mb: 4, px: { xs: 1, sm: 2 } }}>
      <BackButton />

      {/* Header Toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          mt: 2,
          mb: 3,
          borderRadius: 3.5,
          border: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
                System Communications & Support
              </Typography>
              <Chip
                label={LEVEL_TITLES[userLevel] || `Level ${userLevel}`}
                sx={{ bgcolor: `${LEVEL_COLORS[userLevel] || '#8b5cf6'}15`, color: LEVEL_COLORS[userLevel] || '#8b5cf6', fontWeight: 700 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Role-hierarchical messaging dispatch system with pre-defined support heads and status logs
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {can('messages.inbox.compose') && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={() => setComposeOpen(true)}
                sx={{ fontWeight: 700, borderRadius: 2.5, px: 3, py: 1 }}
              >
                Compose Message
              </Button>
            )}
            <IconButton onClick={fetchMessages} color="primary" sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, p: 1 }}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Navigation Tabs & Search Controls */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <Tabs
              value={activeTab}
              onChange={(e, val) => setActiveTab(val)}
              indicatorColor="primary"
              textColor="primary"
              sx={{ minHeight: 44 }}
            >
              <Tab label={`Inbox ${counts.unread > 0 ? `(${counts.unread} unread)` : ''}`} value="inbox" sx={{ fontWeight: 700 }} />
              <Tab label="Sent Messages" value="sent" sx={{ fontWeight: 700 }} />
            </Tabs>
          </Grid>
          <Grid item xs={12} md={7}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              {userLevel === 1 && activeTab === 'inbox' && (
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Sender Level</InputLabel>
                  <Select value={senderLevelFilter} label="Sender Level" onChange={(e) => setSenderLevelFilter(e.target.value)}>
                    <MenuItem value="all">All Sender Levels</MenuItem>
                    <MenuItem value="2">Level 2 (State Head)</MenuItem>
                    <MenuItem value="3">Level 3 (State Officer)</MenuItem>
                    <MenuItem value="4">Level 4 (Q-Pay Centre)</MenuItem>
                    <MenuItem value="5">Level 5 (Q-Pay Agent)</MenuItem>
                  </Select>
                </FormControl>
              )}

              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel>Message Head</InputLabel>
                <Select
                  value={messageHeadFilter}
                  label="Message Head"
                  onChange={(e) => setMessageHeadFilter(e.target.value)}
                >
                  <MenuItem value="all">All Message Heads</MenuItem>
                  {PREDEFINED_MESSAGE_HEADS.map((head) => (
                    <MenuItem key={head} value={head}>{head}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="unread">Unread</MenuItem>
                  <MenuItem value="read">Read</MenuItem>
                  <MenuItem value="replied">Replied</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { xs: '100%', sm: 220 } }}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Message List Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress size={40} />
        </Box>
      ) : messages.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3.5, border: '1px solid #e2e8f0' }}>
          <EmailIcon sx={{ fontSize: 60, color: '#cbd5e1', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" fontWeight={700}>
            No messages found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {activeTab === 'inbox' ? 'Your inbox is currently clear.' : 'You have not dispatched any messages yet.'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing= {2.5}>
          {messages.map((msg) => (
            <Grid item xs={12} key={msg._id}>
              <Card
                elevation={0}
                onClick={() => handleOpenDetail(msg)}
                sx={{
                  borderRadius: 3,
                  border: msg.status === 'unread' ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                  backgroundColor: msg.status === 'unread' ? '#f0f9ff' : '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': { boxShadow: '0 8px 20px rgba(0,0,0,0.06)', transform: 'translateY(-2px)' },
                }}
              >
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: `${LEVEL_COLORS[msg.senderLevel] || '#8b5cf6'}20`,
                          color: LEVEL_COLORS[msg.senderLevel] || '#8b5cf6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 800,
                        }}
                      >
                        {(msg.senderName || 'U')[0].toUpperCase()}
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a' }}>
                            {msg.senderName}
                          </Typography>
                          <Chip
                            label={LEVEL_TITLES[msg.senderLevel] || `Level ${msg.senderLevel}`}
                            size="small"
                            sx={{ bgcolor: `${LEVEL_COLORS[msg.senderLevel] || '#8b5cf6'}15`, color: LEVEL_COLORS[msg.senderLevel] || '#8b5cf6', fontWeight: 700, fontSize: '0.75rem' }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            → To: <strong>{msg.recipientName || `Level ${msg.recipientLevel}`}</strong>
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {msg.senderEmail} {msg.senderMobile ? `• ${msg.senderMobile}` : ''}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {renderStatusChip(msg.status)}
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {new Date(msg.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 2, pl: { xs: 0, sm: 6.5 } }}>
                    <Chip
                      icon={<CategoryIcon style={{ fontSize: 13, color: '#0284c7' }} />}
                      label={msg.messageHead || msg.subject}
                      variant="outlined"
                      size="small"
                      sx={{ fontWeight: 700, borderColor: '#bae6fd', color: '#0369a1', bgcolor: '#f0f9ff', mb: 1 }}
                    />
                    <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500, lineHeight: 1.6, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {msg.message}
                    </Typography>

                    {msg.replyMessage && (
                      <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#166534', display: 'block', mb: 0.5 }}>
                          {Array.isArray(msg.replies) && msg.replies.length > 1 ? `Latest response (${msg.replies.length} replies):` : 'Response:'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#15803d', fontSize: '0.85rem' }}>
                          {msg.replyMessage}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Compose Message Dialog */}
      <Dialog open={composeOpen} onClose={() => setComposeOpen(false)} maxWidth="sm" fullWidth paperprops={{ sx: { borderRadius: 3.5 } }}>
        <DialogTitle sx={{ bg: '#0f172a', color: '#ffffff', fontWeight: 800, py: 2 }}>
          Dispatch Communication Message
        </DialogTitle>
        <form onSubmit={handleSendMessage}>
          <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Target Level Select */}
            <FormControl fullWidth size="small">
              <InputLabel>Recipient Level</InputLabel>
              <Select
                value={targetLevel}
                label="Recipient Level"
                onChange={(e) => setTargetLevel(Number(e.target.value))}
              >
                {allowedRecipientLevels.map((lvl) => (
                  <MenuItem key={lvl} value={lvl}>
                    {LEVEL_TITLES[lvl]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Target User Autocomplete (Optional) */}
            {targetLevel !== 1 && (
              <Autocomplete
                options={recipientUsers}
                getOptionLabel={(option) => `${option.firstName || ''} ${option.lastName || ''} (${option.email || ''})`}
                value={selectedRecipient}
                onChange={(e, val) => setSelectedRecipient(val)}
                loading={fetchingRecipients}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Specific User Recipient (Optional - leave blank for all)"
                    placeholder="Search user..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {fetchingRecipients ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            )}

            {/* Message Head Pre-defined Select */}
            <FormControl fullWidth size="small">
              <InputLabel>Message Head / Category</InputLabel>
              <Select
                value={messageHead}
                label="Message Head / Category"
                onChange={(e) => setMessageHead(e.target.value)}
              >
                {PREDEFINED_MESSAGE_HEADS.map((head, idx) => (
                  <MenuItem key={idx} value={head}>
                    {head}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Custom Subject for 'Other' */}
            {messageHead === 'Other' && (
              <TextField
                fullWidth
                size="small"
                label="Specify Custom Subject / Head"
                value={customSubject}
                onChange={(e) => setCustomSubject(e.target.value)}
                required
              />
            )}

            {/* Message Content Body */}
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Detailed Message Description"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Describe your issue or query clearly..."
              required
            />
          </DialogContent>
          <DialogActions sx={{ p: 2.5, bg: '#f8fafc' }}>
            <Button onClick={() => setComposeOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={sending} startIcon={<SendIcon />}>
              {sending ? 'Sending...' : 'Send Message'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* View & Reply Detail Dialog */}
      {selectedMessage && (
        <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth paperprops={{ sx: { borderRadius: 3.5 } }}>
          <DialogTitle sx={{ bgcolor: '#0f172a', color: '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={800} sx={{ color: '#ffffff' }}>
              {selectedMessage.subject || 'Message Details'}
            </Typography>
            <Chip
              label={selectedMessage.messageHead || selectedMessage.subject}
              color="primary"
              sx={{ fontWeight: 700 }}
            />
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    From:
                  </Typography>
                  <Typography variant="body1" fontWeight={700}>
                    {selectedMessage.senderName} ({LEVEL_TITLES[selectedMessage.senderLevel] || `Level ${selectedMessage.senderLevel}`})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {selectedMessage.senderEmail} • {selectedMessage.senderMobile}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    To:
                  </Typography>
                  <Typography variant="body1" fontWeight={700}>
                    {selectedMessage.recipientName || `Level ${selectedMessage.recipientLevel}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Sent: {new Date(selectedMessage.createdAt).toLocaleString('en-IN')}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Conversation thread: original message + all replies (time order) */}
            <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>
              Conversation
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                p: 2,
                mb: 3,
                bgcolor: '#f8fafc',
                borderRadius: 2.5,
                border: '1px solid #e2e8f0',
                maxHeight: 420,
                overflowY: 'auto',
              }}
            >
              {buildThread(selectedMessage).map((entry) => {
                const mine = !entry.isLegacy && entry.senderId && userId && String(entry.senderId) === String(userId);
                const color = LEVEL_COLORS[entry.senderLevel] || '#16a34a';
                const levelName = entry.senderLevel ? (LEVEL_NAMES[entry.senderLevel] || `Level ${entry.senderLevel}`) : '';
                const author = entry.isLegacy
                  ? 'Reply'
                  : (mine ? 'You' : (entry.senderName || (entry.isOriginal ? 'Sender' : 'Reply')));
                return (
                  <Box key={entry.key} sx={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                    <Box
                      sx={{
                        maxWidth: { xs: '92%', sm: '78%' },
                        px: 2,
                        py: 1.25,
                        borderRadius: 2.5,
                        borderTopLeftRadius: mine ? 10 : 2,
                        borderTopRightRadius: mine ? 2 : 10,
                        bgcolor: mine ? '#e0f2fe' : (entry.isOriginal ? '#ffffff' : '#f0fdf4'),
                        border: `1px solid ${mine ? '#bae6fd' : (entry.isOriginal ? '#e2e8f0' : '#bbf7d0')}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#0f172a' }}>
                          {author}
                        </Typography>
                        {levelName && (
                          <Chip
                            label={levelName}
                            size="small"
                            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: `${color}15`, color }}
                          />
                        )}
                        {entry.isOriginal && (
                          <Chip label="Original" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ color: '#1e293b', whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                        {entry.message}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: mine ? 'right' : 'left' }}>
                        {formatIST(entry.createdAt) || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Reply Input Box (For Recipients / SuperAdmin) */}
            {can('messages.inbox.reply') && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Send Response / Counter Reply:
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Type your response here..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, bgcolor: '#f8fafc', justifyContent: 'space-between' }}>
            {can('messages.inbox.delete') ? (
              <Button
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => handleDeleteMessage(selectedMessage._id)}
              >
                Delete
              </Button>
            ) : <span />}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => setViewDialogOpen(false)} color="inherit">
                Close
              </Button>
              {can('messages.inbox.reply') && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<ReplyIcon />}
                  disabled={replying || !replyText.trim()}
                  onClick={handleSendReply}
                >
                  {replying ? 'Sending Reply...' : 'Send Reply'}
                </Button>
              )}
            </Box>
          </DialogActions>
        </Dialog>
      )}

      {/* Snackbar feedback */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackOpen(false)} severity={snackSeverity} sx={{ width: '100%' }}>
          {snackMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserMessagesPage;
